
import {
  Component, Ref, Watch, Mixins, PropSync, Prop, Inject,
} from 'vue-property-decorator';
import { InputValidationRules } from 'vuetify';
import {
  CreateShipmentValidationState,
  rulesIfAttempted as rulesIfAttemptedFn,
} from '@/features/shipment/helpers/createShipmentValidation';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { VForm } from '@/features/common/types';
import { deepClone } from '@/features/common/helpers';
import { EstimateDataType, EstimateShipAddressType } from '@/features/estimate/types';
import SwapButtonNew from '@/features/shipment/components/createShipment/SwapButtonNew.vue';
import { requiredRules } from '@/features/common/helpers/validationRules';
import { UserPreferences } from '@/features/settings/types';
import { CityProvinceLookupMixin } from '@/features/shipment/components/mixins';
import { UserInfo } from '@/features/users/types';
import { ClarificationAddressDto } from '@/features/shipment/types';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import { loadEstimateDataFromLocalstorage } from '../helpers/getEstimate';

@Component({
  components: {
    SwapButtonNew,
  },
})
export default class WhereShipment extends Mixins(CityProvinceLookupMixin, ResponsivenessMixin) {
  @Inject({
    from: 'createShipmentValidationState',
    default: () => ({ attempted: true }),
  })
  private createShipmentValidationState!: CreateShipmentValidationState;

  @PropSync('from', { required: true }) fromData!: EstimateShipAddressType;

  @PropSync('to', { required: true }) toData!: EstimateShipAddressType;

  @Prop({ required: true }) showFromCityInput!: boolean;

  @Prop({ required: true }) showToCityInput!: boolean;

  @Ref('form') form!: VForm;

  @Ref('toPostalCodeInput') toPostalCodeInput!: HTMLFormElement;

  @Ref('fromPostalCodeInput') fromPostalCodeInput!: HTMLFormElement;

  private requiredRules = requiredRules;

  private rulesIfAttempted(rules?: InputValidationRules): InputValidationRules {
    return rulesIfAttemptedFn(rules, this.createShipmentValidationState.attempted);
  }

  get estimateDataFromStore(): EstimateDataType {
    return this.$store.state.estimate.estimateData;
  }

  get isUserPreferences(): number {
    return this.$store.getters.getIsUserPreferences;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get userInfo(): UserInfo {
    return this.$store.getters.getUserInfo;
  }

  @Watch('isUserPreferences', { immediate: true })
  private async populateUserPrefsData(newValue: number, isClearedManually?: boolean)
  : Promise<void> {
    if (!newValue) return;

    const {
      fromAddress,
      toAddress,
    } = this.estimateDataFromStore;

    if (fromAddress.countryCode || fromAddress.postalCode || fromAddress.city) {
      this.fromData = { ...fromAddress };
    } else {
      if (isClearedManually || await this.populateEstimateDataFromLocalstorageIfAvailable()) return;

      const {
        defaultAddress,
      } = this.userPreferences.shippingSettings;

      if (defaultAddress) {
        const { countryCode, postalCode } = defaultAddress;
        this.fromData = {
          countryCode: countryCode || '',
          postalCode: postalCode || '',
          isInside: false,
          isLimitedAccess: false,
          isTailGate: false,
          isTradeShow: false,
        };
        this.updateEstimateDataToStore('fromAddress', { ...this.fromData });
      }
    }

    if (
      toAddress.countryCode
        || toAddress.postalCode
        || toAddress.residential
        || fromAddress.city
    ) {
      this.toData = { ...toAddress };
    }
  }

  created(): void {
    eventBus.$on(eventNames.CLEAR_ESTIMATE_FORM, this.clearData);
  }

  mounted(): void {
    this.$store.commit('setWhereEstimateForm', this.form);
  }

  // eslint-disable-next-line class-methods-use-this
  destroyed(): void {
    eventBus.$off(eventNames.CLEAR_ESTIMATE_FORM);
  }

  private async populateEstimateDataFromLocalstorageIfAvailable(): Promise<boolean> {
    if (this.userInfo.isLC) return false;

    const { fromAddresses, toAddresses } = loadEstimateDataFromLocalstorage();

    if (!fromAddresses && !toAddresses) return false;

    const {
      fromAddress: currentFromAddress,
      toAddress: currentToAddress,
    } = this.estimateDataFromStore;

    const isValidAddress = (address: ClarificationAddressDto | undefined): boolean => {
      return !!address?.countryCode && !!address?.postalCode;
    };

    let populated = false;

    if (isValidAddress(fromAddresses)) {
      this.fromData = { ...currentFromAddress, ...fromAddresses };
      populated = true;
    }

    if (isValidAddress(toAddresses)) {
      this.toData = { ...currentToAddress, ...toAddresses };
      populated = true;
    }

    return populated;
  }

  private updateEstimateDataToStore(key: string, value: unknown) {
    this.$store.commit('patchEstimateData', { key, value });
  }

  private clearData() {
    if (this.$refs.form) {
      (this.$refs.form as HTMLFormElement).resetValidation();
    }

    this.toData = {
      countryCode: '',
      postalCode: '',
      city: '',
      residential: false,
      isInside: false,
      isLimitedAccess: false,
      isTailGate: false,
      isTradeShow: false,
    };

    this.$store.commit('setEstimateData', {
      fromAddress: {
        addr1: '',
        countryCode: '',
        postalCode: '',
        city: '',
        isInside: false,
        isLimitedAccess: false,
        isTailGate: false,
        isTradeShow: false,
      },
      toAddress: {
        addr1: '',
        countryCode: '',
        postalCode: '',
        city: '',
        residential: false,
        isInside: false,
        isLimitedAccess: false,
        isTailGate: false,
        isTradeShow: false,
      },
      signatureOptions: '',
      packages: [],
      isDocumentsOnly: false,
    });

    this.populateUserPrefsData(Date.now(), true);
  }

  private async handleAutofillResidential(isValid: boolean) {
    this.updateEstimateDataToStore('toAddress', { ...this.toData });

    if (!isValid) return;

    const fill = (residential: boolean) => {
      this.toData.residential = residential || this.toData.residential;
    };

    await this.autofillResidential(fill, 'toData');

    this.updateEstimateDataToStore('toAddress', { ...this.toData });
  }

  private handleSwap(): void {
    (this.$refs.form as HTMLFormElement).resetValidation();
    const fromAddressTemp = deepClone(this.fromData);
    const fromAddress = this.toData;
    const toAddress = fromAddressTemp;

    this.fromData = fromAddress;
    this.toData = toAddress;
    this.updateEstimateDataToStore('fromAddress', fromAddress);
    this.updateEstimateDataToStore('toAddress', toAddress);
  }

  private async handleCountryInputTabPress(
    event: Event,
    address: 'from' | 'to',
    defaultValue?: string,
  ) {
    event.preventDefault();
    await this.$nextTick();

    if (address === 'from') {
      this.fromPostalCodeInput.$children[0].focus();
      if (!this.fromData.countryCode && defaultValue) {
        this.fromData.countryCode = defaultValue;
      }
    } else if (address === 'to') {
      this.toPostalCodeInput.$children[0].focus();
      if (!this.toData.countryCode && defaultValue) {
        this.toData.countryCode = defaultValue;
      }
    }
  }
}
